import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaFilePdf } from 'react-icons/fa';

const AuditWorkDetails = () => {
    const { category, id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [work, setWork] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const dateParam = queryParams.get('date');

        let workPath;
        if (dateParam) {
            // Fetch from generatework node
            workPath = category === 'General' || !category 
                ? `ekamwatapzp/generatework/${dateParam}/${id}` 
                : `ekamwatapzp/generatework/${dateParam}/${category}/${id}`;
        } else {
            // Fetch from standard WorkNames node
            workPath = category === 'General' || !category 
                ? `ekamwatapzp/WorkNames/${id}` 
                : `ekamwatapzp/WorkNames/${category}/${id}`;
        }

        const workRef = ref(db, workPath);
        const unsubscribe = onValue(workRef, (snapshot) => {
            if (snapshot.exists()) {
                setWork(snapshot.val());
            } else {
                setWork(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [category, id, location.search]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    if (!work) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#f4f7fe] p-4 text-center">
                <h2 className="text-xl font-bold text-gray-800 mb-4">माहिती सापडली नाही!</h2>
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 px-6 py-2 bg-[#4e73df] text-white rounded-lg hover:bg-opacity-90 transition font-bold shadow-md"
                >
                    <FaArrowLeft /> मागे जा
                </button>
            </div>
        );
    }

    const DetailRow = ({ label1, value1, label2, value2 }) => (
        <tr className="border-b border-gray-100">
            <td className="w-[20%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">
                {label1}
            </td>
            <td className="w-[30%] px-4 py-3 text-[13px] text-gray-600 font-bold border-r border-gray-200">
                {value1 || '-'}
            </td>
            <td className="w-[20%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">
                {label2}
            </td>
            <td className="w-[30%] px-4 py-3 text-[13px] text-gray-600 font-bold">
                {value2 || '-'}
            </td>
        </tr>
    );

    const PdfRow = ({ label1, url1, label2, url2 }) => (
        <tr className="border-b border-gray-100">
            <td className="w-[20%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">
                {label1}
            </td>
            <td className="w-[30%] px-4 py-3 border-r border-gray-200">
                {url1 ? (
                    <div 
                        className="p-1 border border-gray-200 rounded w-fit cursor-pointer hover:bg-gray-100 transition shadow-sm"
                        onClick={() => window.open(url1, '_blank')}
                    >
                        <FaFilePdf className="text-red-500 text-lg" />
                    </div>
                ) : '-'}
            </td>
            <td className="w-[20%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">
                {label2}
            </td>
            <td className="w-[30%] px-4 py-3">
                {url2 ? (
                    <div 
                        className="p-1 border border-gray-200 rounded w-fit cursor-pointer hover:bg-gray-100 transition shadow-sm"
                        onClick={() => window.open(url2, '_blank')}
                    >
                        <FaFilePdf className="text-red-500 text-lg" />
                    </div>
                ) : '-'}
            </td>
        </tr>
    );

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 max-w-[1300px] mx-auto gap-4 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#4e73df] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800">अर्जदाराची कागदपत्रे</h1>
                </div>
                <div className="text-[12px] font-bold text-gray-400">
                    सांगली जिल्हा परिषद / <span className="text-blue-500">अर्जदाराची कागदपत्रे</span>
                </div>
            </div>

            <div className="max-w-[1300px] mx-auto bg-white rounded shadow-lg border border-gray-100 relative overflow-hidden">
                <div className="p-6 md:p-10">
                    <div className="mb-6 pb-2 border-b-2 border-gray-100">
                        <h2 className="text-[20px] font-bold text-[#42a5f5] leading-relaxed">
                            कामाचे नाव - {work.workName}
                        </h2>
                    </div>

                    <div className="border border-gray-200 rounded shadow-sm overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <tbody>
                                <DetailRow 
                                    label1="तालुका" value1={work.taluka} 
                                    label2="योजनेचे नाव" value2={work.schemeName}
                                />
                                <DetailRow 
                                    label1="निविदा रक्कम" value1={work.tenderAmount} 
                                    label2="जावक नंबर / प्रमा नंबर / दिनांक" value2={`${work.inwardNo || '-'} ${work.inwardDate || ''}`}
                                />
                                <DetailRow 
                                    label1="दिनांक" value1={work.date} 
                                    label2="प्रशासकीय मंजुरीचा अधिकार" value2={work.adminApprovalAuthority}
                                />
                                <DetailRow 
                                    label1="जावक.क्र दिनांक" value1={work.inwardDate} 
                                    label2="तांत्रिक मंजूर आदेश क्र." value2={work.technicalApprovalOrderNo}
                                />
                                <DetailRow 
                                    label1="अंदाजपत्रकीय तरतूद सन" value1={work.budgetaryProvisionYear} 
                                    label2="काम पुर्ण करणेची मुदत" value2={work.completionPeriod ? `${work.completionPeriod} दिवस` : '-'}
                                />
                                <DetailRow 
                                    label1="टेंडरचा प्रकार" value1={work.tenderType || 'ब-1'} 
                                    label2="टेंडर नोटीस कार्यवाही" value2={work.tenderNoticeAction}
                                />
                                <DetailRow 
                                    label1="Work Portion" value1={work.recapitulation?.workPortion} 
                                    label2="" value2=""
                                />
                                <DetailRow 
                                    label1="Insurance" value1={work.recapitulation?.insurance || '0'} 
                                    label2="GST" value2={work.recapitulation?.gst}
                                />
                                <DetailRow 
                                    label1="Other" value1={work.recapitulation?.other || '0'} 
                                    label2="" value2=""
                                />
                                <PdfRow 
                                    label1="AA Upload" url1={work.aaUploadUrl} 
                                    label2="Recap Upload" url2={work.recapUploadUrl}
                                />
                                <PdfRow 
                                    label1="TS Upload" url1={work.tsUploadUrl} 
                                    label2="DTP Upload" url2={work.dtpUploadUrl}
                                />
                             </tbody>
                        </table>
                    </div>

                    <div className="mt-10 border-t-[4px] border-[#1a1a4d] no-print"></div>
                </div>
            </div>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .p-4, .p-8 { padding: 0 !important; }
                    .shadow-lg { box-shadow: none !important; }
                    .border { border: 1px solid #efefef !important; }
                    table { border: 1px solid #ddd !important; }
                    td { padding: 8px !important; }
                    .bg-[#f8f9fa] { background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; }
                }
            `}</style>
        </div>
    );
};

export default AuditWorkDetails;
